// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { memo } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import { mdiFilterPlus } from "@mdi/js";
import {
  filterWellsFromList,
  removeWellListFilter,
  setFilterState
} from "store/features";
import { RootState } from "store/rootReducer";
import { type ISelectedWell, selectMapState, useMapStore } from "stores/map";
import { useXdaFilterRevertStore } from "stores/xdaFilterRevertStore";

import { ToolbarButton } from "components/base";
import { mergeFilterWellList } from "components/vis/util/ducWells";
import { isLassoFilterPolygon } from "components/vis/utils/isLassoFilterPolygon";

interface XdaFilterToWellToggleProps {
  selectedWells: { [name: string]: ISelectedWell };
  /**
   * EVA-185: UWIs of the dot-only wells in the current section. They are not in
   * `selectedWells` (the filter usually excludes them), but "Filter to wells"
   * must bring them along whenever the dots are showing.
   */
  additionalWells?: readonly string[];
}

// Marks a well-list filter as applied by the XDA toolbar. Stored on the filter's
// `source` field (not `filterType`, which map-click selection keys off "well-list")
// so this toolbar recognizes only its own filter — filters applied elsewhere
// (lasso, well list, folders, forecasting) leave the button inactive.
const XDA_FILTER_SOURCE = "xda";

const selectXdaWellListFilter = (state: RootState) =>
  state.filter.wellListFilter.find((filter) => filter?.source === XDA_FILTER_SOURCE);

const XdaFilterToWellToggle = ({
  selectedWells,
  additionalWells = []
}: XdaFilterToWellToggleProps) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const wellList = mergeFilterWellList(Object.keys(selectedWells), additionalWells);

  // Derived from Redux, so the active state survives the toolbar's unmount/remount
  // when entering/exiting screenshot mode, and an externally-cleared or -replaced
  // filter drops the tag and deactivates the button automatically.
  const xdaWellListFilter = useSelector(selectXdaWellListFilter);
  const isFilterActive = Boolean(xdaWellListFilter);

  const handleToggle = () => {
    const revertStore = useXdaFilterRevertStore.getState();

    if (isFilterActive) {
      const { snapshot } = revertStore;
      // Only revert when the snapshot was captured for THIS active XDA filter.
      // A mismatch means the xda-tagged filter came from elsewhere (saved filter,
      // history restore, another tab), so there is nothing valid to revert to.
      if (snapshot && snapshot.forFilterId === xdaWellListFilter.id) {
        dispatch(setFilterState(snapshot.filterState));
        if (isLassoFilterPolygon(snapshot.filterState.polygonFilter)) {
          useMapStore.getState().setViewLock(true);
        }
        // Fly back to the exact pre-filter camera. Restoring the precise view
        // reproduces the original extent, so the filtered count is preserved — a
        // padded fitBounds would widen the extent and inflate the count.
        useMapStore.getState().setFlyToSpecificPoint(snapshot.mapState);
      } else {
        dispatch(removeWellListFilter({ id: xdaWellListFilter.id }));
      }
      revertStore.clearSnapshot();
      return;
    }

    if (wellList.length === 0) return;

    // Capture the filter + camera being replaced BEFORE applying (both read
    // lazily so panning the map doesn't re-render this button).
    const { filter } = store.getState();
    const filterState = {
      polygonFilter: filter.polygonFilter,
      excludePolygonsFilter: [...filter.excludePolygonsFilter],
      wellListFilter: [...filter.wellListFilter],
      propertiesFilter: [...filter.propertiesFilter]
    };
    const { lat, lng, zoom } = selectMapState(useMapStore.getState());

    dispatch(filterWellsFromList({ wellList, source: XDA_FILTER_SOURCE }));

    // Tag the snapshot with the id the reducer just generated for the applied
    // entry, so the revert can confirm the snapshot still matches it.
    const appliedFilterId = selectXdaWellListFilter(store.getState())?.id;
    if (appliedFilterId) {
      revertStore.setSnapshot({
        filterState,
        mapState: { lat, lng, zoom },
        forFilterId: appliedFilterId
      });
    }
  };

  const selectedCount = wellList.length;
  const isDisabled = !isFilterActive && selectedCount === 0;

  return (
    <ToolbarButton
      icon={<Icon path={mdiFilterPlus} size={1.75} />}
      active={isFilterActive}
      tooltipText={
        isFilterActive ? "Clear well filter" : `Filter to wells (${selectedCount})`
      }
      overflowLabel={isFilterActive ? "Clear well filter" : "Filter to wells"}
      data-testid="xda-filter-to-wells-toggle"
      styleKey="xdaToolbar"
      disabled={isDisabled}
      onClick={handleToggle}
    />
  );
};

export default memo(XdaFilterToWellToggle);
